import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import {
  fetchGoalMonth,
  saveGoalMonth,
  type MarketingGoalCounts,
  type MarketingGoalWeek,
} from "@/services/marketing";
import { cn } from "@/utils/cn";
import {
  GOAL_KINDS,
  buildAutoPlan,
  emptyCounts,
  goalKindLabel,
  rebalanceToMonth,
  validateGoalPlan,
  weekLabel,
  type DistributionBasis,
} from "./marketingGoalPlan";

interface Props {
  open: boolean;
  initialMonth: string;
  onClose: () => void;
  onSaved: () => void;
}

const KIND_LIST = [...GOAL_KINDS];

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export function MarketingGoalsModal({ open, initialMonth, onClose, onSaved }: Props) {
  const [month, setMonth] = useState(initialMonth || currentMonth());
  const [counts, setCounts] = useState<MarketingGoalCounts>(emptyCounts());
  const [basis, setBasis] = useState<DistributionBasis>("calendar");
  const [includeSaturday, setIncludeSaturday] = useState(false);
  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [weeks, setWeeks] = useState<MarketingGoalWeek[]>([]);
  const [expanded, setExpanded] = useState<number | null>(1);
  const [reason, setReason] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [confirmPast, setConfirmPast] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<Array<{ version: number; savedAt: string; savedByName: string; reason: string; changes: string[] }>>([]);
  const [previous, setPrevious] = useState<{ counts: MarketingGoalCounts; distribution: { basis: DistributionBasis; includeSaturday: boolean } } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualDirty, setManualDirty] = useState(false);
  const [pendingCounts, setPendingCounts] = useState<MarketingGoalCounts | null>(null);

  const locked = readOnly && !confirmPast;
  const validation = useMemo(() => validateGoalPlan(counts, weeks), [counts, weeks]);

  const applyAuto = useCallback(
    (nextCounts: MarketingGoalCounts, nextBasis: DistributionBasis, saturday: boolean) => {
      setWeeks(buildAutoPlan({ month, counts: nextCounts, basis: nextBasis, includeSaturday: saturday }));
      setMode("auto");
      setManualDirty(false);
    },
    [month],
  );

  const load = useCallback(async () => {
    if (!open || !month) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGoalMonth(month);
      setReadOnly(data.readOnly);
      setConfirmPast(false);
      setHistory(data.history ?? []);
      setPrevious(
        data.previousMonth
          ? {
              counts: data.previousMonth.counts,
              distribution: {
                basis: data.previousMonth.distribution.basis,
                includeSaturday: data.previousMonth.distribution.includeSaturday,
              },
            }
          : null,
      );
      if (data.version) {
        setCounts(data.version.counts);
        setBasis(data.version.distribution.basis);
        setIncludeSaturday(data.version.distribution.includeSaturday);
        setMode(data.version.distribution.mode);
        setWeeks(data.version.weeks);
        setManualDirty(data.version.distribution.mode === "manual");
      } else {
        const blank = emptyCounts();
        setCounts(blank);
        setBasis("calendar");
        setIncludeSaturday(false);
        setWeeks(buildAutoPlan({ month, counts: blank, basis: "calendar", includeSaturday: false }));
        setMode("auto");
        setManualDirty(false);
      }
      setReason("");
      setPendingCounts(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar metas.");
    } finally {
      setLoading(false);
    }
  }, [open, month]);

  useEffect(() => {
    if (!open) return;
    setMonth(initialMonth || currentMonth());
    setHistoryOpen(false);
  }, [open, initialMonth]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateKind = (kind: string, value: string) => {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    const next = { ...(pendingCounts ?? counts), [kind]: n };
    if (mode === "manual" && manualDirty) {
      setPendingCounts(next);
      return;
    }
    setCounts(next);
    if (mode === "auto") applyAuto(next, basis, includeSaturday);
  };

  const acceptRedistribute = (all: boolean) => {
    if (!pendingCounts) return;
    if (all) {
      setCounts(pendingCounts);
      applyAuto(pendingCounts, basis, includeSaturday);
    } else {
      setCounts(pendingCounts);
      setWeeks(rebalanceToMonth(weeks, pendingCounts, basis, includeSaturday));
      setMode("manual");
    }
    setPendingCounts(null);
  };

  const setDayValue = (weekIndex: number, date: string, kind: string, value: string) => {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setMode("manual");
    setManualDirty(true);
    setWeeks((current) =>
      current.map((week) => {
        if (week.index !== weekIndex) return week;
        const days = week.days.map((day) =>
          day.date === date ? { ...day, counts: { ...day.counts, [kind]: n } } : day,
        );
        const counts = KIND_LIST.reduce<MarketingGoalCounts>((acc, key) => {
          acc[key] = days.reduce((sum, day) => sum + (day.counts[key] ?? 0), 0);
          return acc;
        }, {});
        return { ...week, days, counts: { ...week.counts, ...counts } };
      }),
    );
  };

  const setWeekValue = (weekIndex: number, kind: string, value: string) => {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setMode("manual");
    setManualDirty(true);
    setWeeks((current) =>
      current.map((week) =>
        week.index === weekIndex ? { ...week, counts: { ...week.counts, [kind]: n } } : week,
      ),
    );
  };

  const handleSave = async () => {
    if (!validation.ok) {
      setError(validation.messages[0] ?? "A soma das metas não fecha.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveGoalMonth({
        month,
        counts,
        distribution: { mode, basis, includeSaturday },
        weeks,
        reason,
        confirmPastEdit: confirmPast,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnEscape
      closeOnBackdropClick
      size="xl"
      title="Metas de produção"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={locked || loading || !validation.ok}>
            Salvar metas
          </Button>
        </div>
      }
    >
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
          <Input label="Mês de referência" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          <p className="self-end text-sm text-slate-600">
            A meta do mês é desdobrada nas semanas e nos dias. A soma precisa fechar exatamente.
          </p>
        </div>

        {readOnly && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Este mês já encerrou e está somente leitura.
            {!confirmPast && (
              <button type="button" className="ml-2 font-semibold underline" onClick={() => setConfirmPast(true)}>
                Editar mesmo assim
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {KIND_LIST.map((kind) => (
            <Input
              key={kind}
              label={`Meta do mês · ${goalKindLabel(kind)}`}
              type="number"
              min={0}
              value={String((pendingCounts ?? counts)[kind] ?? 0)}
              disabled={locked || loading}
              onChange={(e) => updateKind(kind, e.target.value)}
            />
          ))}
        </div>

        {pendingCounts && (
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-slate-700">
            A meta do mês mudou depois do ajuste manual. Como aplicar?
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => acceptRedistribute(true)}>
                Redistribuir tudo
              </Button>
              <Button size="sm" variant="secondary" onClick={() => acceptRedistribute(false)}>
                Só a diferença
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPendingCounts(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "auto"}
              disabled={locked}
              onChange={() => applyAuto(counts, basis, includeSaturday)}
            />
            Distribuir automaticamente
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "manual"}
              disabled={locked}
              onChange={() => setMode("manual")}
            />
            Ajustar manualmente
          </label>
          <select
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            value={basis}
            disabled={locked}
            onChange={(e) => {
              const next = e.target.value === "weekdays" ? "weekdays" : "calendar";
              setBasis(next);
              if (mode === "auto") applyAuto(counts, next, includeSaturday);
            }}
          >
            <option value="calendar">Dias corridos</option>
            <option value="weekdays">Dias úteis (seg–sex)</option>
          </select>
          {basis === "weekdays" && (
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeSaturday}
                disabled={locked}
                onChange={(e) => {
                  setIncludeSaturday(e.target.checked);
                  if (mode === "auto") applyAuto(counts, basis, e.target.checked);
                }}
              />
              Incluir sábado
            </label>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={locked || !previous}
            onClick={() => {
              if (!previous) return;
              setCounts(previous.counts);
              setBasis(previous.distribution.basis);
              setIncludeSaturday(previous.distribution.includeSaturday);
              applyAuto(previous.counts, previous.distribution.basis, previous.distribution.includeSaturday);
            }}
          >
            Copiar metas do mês anterior
          </Button>
        </div>

        {!validation.ok && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <ul className="list-inside list-disc">
              {validation.messages.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <Button
              size="sm"
              className="mt-2"
              variant="secondary"
              disabled={locked}
              onClick={() => {
                setWeeks(rebalanceToMonth(weeks, counts, basis, includeSaturday));
                setMode("manual");
                setManualDirty(true);
              }}
            >
              Redistribuir diferença automaticamente
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {weeks.map((week) => (
            <div key={week.index} className="rounded-xl border border-slate-200">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => setExpanded((current) => (current === week.index ? null : week.index))}
              >
                <span className="text-sm font-semibold text-slate-800">{weekLabel(week)}</span>
                <span className="text-xs text-slate-500">{expanded === week.index ? "Recolher" : "Ver dias"}</span>
              </button>
              <div className="grid grid-cols-3 gap-2 px-3 pb-3">
                {KIND_LIST.map((kind) => (
                  <Input
                    key={kind}
                    label={goalKindLabel(kind)}
                    type="number"
                    min={0}
                    value={String(week.counts[kind] ?? 0)}
                    disabled={locked || mode === "auto"}
                    onChange={(e) => setWeekValue(week.index, kind, e.target.value)}
                  />
                ))}
              </div>
              {expanded === week.index && (
                <div className="border-t border-slate-100 px-3 py-2">
                  {week.days.map((day) => (
                    <div key={day.date} className="grid grid-cols-[88px_1fr_1fr_1fr] items-center gap-2 py-1">
                      <span className="text-xs font-medium text-slate-600">
                        {day.date.slice(8, 10)}/{day.date.slice(5, 7)}
                      </span>
                      {KIND_LIST.map((kind) => (
                        <input
                          key={kind}
                          type="number"
                          min={0}
                          aria-label={`${day.date} ${goalKindLabel(kind)}`}
                          disabled={locked || mode === "auto"}
                          value={day.counts[kind] ?? 0}
                          onChange={(e) => setDayValue(week.index, day.date, kind, e.target.value)}
                          className={cn(
                            "w-full rounded-lg border border-slate-200 px-2 py-1 text-sm",
                            (locked || mode === "auto") && "bg-slate-50 text-slate-500",
                          )}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <Input
          label="Motivo da alteração (opcional)"
          value={reason}
          disabled={locked}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex.: ajuste por campanha"
        />

        <button type="button" className="self-start text-sm font-semibold text-brand-700" onClick={() => setHistoryOpen((v) => !v)}>
          {historyOpen ? "Ocultar histórico" : "Ver histórico de alterações"}
        </button>
        {historyOpen && (
          <ul className="flex flex-col gap-2 text-sm">
            {history.length === 0 && <li className="text-slate-500">Nenhuma versão salva neste mês.</li>}
            {[...history].reverse().map((row) => (
              <li key={row.version} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <p className="font-semibold text-slate-800">
                  Versão {row.version} · {new Date(row.savedAt).toLocaleString("pt-BR")} · {row.savedByName}
                </p>
                {row.reason && <p className="text-slate-600">{row.reason}</p>}
                <p className="text-xs text-slate-500">{row.changes.join(" · ")}</p>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
